import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

const TopOrganizationsChart = ({ organizations = [] }) => {
  // Data is already sorted and limited by API
  const topOrganizations = organizations.map((org) => ({
    name: org.name,
    projects: org.projectCount,
    rank: org.rank,
  }));

  // Color gradient for bars - from orange to blue
  const getBarColor = (index) => {
    const colors = [
      "#F97316", // Orange
      "#FB923C", // Light Orange
      "#FDBA74", // Lighter Orange
      "#FCD34D", // Yellow Orange
      "#FDE047", // Yellow
      "#A3E635", // Yellow Green
      "#4ADE80", // Green
      "#34D399", // Teal
      "#22D3EE", // Cyan
      "#3B82F6", // Blue
    ];
    return colors[index] || "#3B82F6";
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 border-2 border-gray-200 rounded-lg shadow-xl">
          <p className="font-bold text-gray-900 text-base mb-2">{data.name}</p>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-[#F97316]"></div>
            <p className="text-gray-700 text-base font-bold">
              {data.projects} {data.projects === 1 ? 'Project' : 'Projects'}
            </p>
          </div>
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-600 font-semibold">
              Rank #{data.rank}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom label for bars
  const renderCustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    return (
      <text
        x={x + width + 8}
        y={y + height / 2}
        fill="#1F2937"
        textAnchor="start"
        dominantBaseline="middle"
        className="text-sm font-bold"
      >
        {value}
      </text>
    );
  };

  if (!topOrganizations || topOrganizations.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-400 text-sm">No organization data available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={topOrganizations}
          layout="vertical"
          margin={{ top: 5, right: 40, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={true} vertical={false} />
          <XAxis 
            type="number" 
            stroke="#9CA3AF"
            tick={{ fill: '#6B7280', fontSize: 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            stroke="#9CA3AF"
            width={160}
            tick={{ fill: '#1F2937', fontSize: 12, fontWeight: 600 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickFormatter={(value) => {
              // Truncate long names
              return value.length > 20 ? value.substring(0, 20) + '...' : value;
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }} />
          <Bar 
            dataKey="projects" 
            radius={[0, 8, 8, 0]}
            maxBarSize={35}
          >
            {topOrganizations.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getBarColor(index)}
                style={{
                  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))',
                }}
              />
            ))}
            <LabelList 
              content={renderCustomLabel}
              position="right"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TopOrganizationsChart;

